'use client';

import type { ChainId } from '@morpho-org/blue-sdk';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button, Icon, Separator, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Search } from '@repo/ui/icons';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Command } from 'cmdk';
import { Popover } from 'radix-ui';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import AdvancedFilterItem from './AdvancedFilterItem';
import { TriggerButton } from './TriggerButton';
export interface FilterItem<T> {
  id: string;
  label: string;
  value: T;
  isChecked: boolean;
  img?: string | null;
  /** Rendered in place of `img` when set. */
  icon?: ReactNode;
  chain?: {
    id: ChainId;
    name: string;
  };
  searchableFields?: string[];
  /** Total matching result count for this option, shown right-aligned when defined. */
  count?: number;
  /** Short highlight label (e.g. "New") shown as an info badge right after the label. */
  tag?: string;
}
interface AdvancedFilterProps<T> {
  items: FilterItem<T>[];
  toggleItem: (value: T) => void;
  sortItems?: () => void;
  clear: () => void;
  inputPlaceholder: string;
  clearTooltipText: string;
  slot?: ReactNode;
  withSeparator?: boolean;
  disabled: boolean;
  width?: string;
  hasHighestZIndex?: boolean;
  side?: 'bottom' | 'left' | 'right' | 'top';
  align?: 'start' | 'center' | 'end';
  /** Trigger label when no item is selected (v5 parity, defaults to "All") */
  allLabel?: string;
  /** Preferred max height of the scrollable item list (v5 parity, defaults to 360px). Clamped to
   * what the viewport leaves for the popover. */
  maxHeight?: string;
  /** Footer text describing the filtered result set (e.g. "12 Vaults"). Falls back to "N selected". */
  resultLabel?: ReactNode;
  /**
   * Whether a filter is active. Defaults to whether any visible item is checked, which undercounts
   * when the caller hides selected items (an asset on a network the user has since deselected).
   */
  hasSelection?: boolean;
}

/** Search row, separator and slot, before the ref callback measures them. */
const DEFAULT_HEADER_HEIGHT = 78;
function AdvancedFilter<T>({
  items,
  toggleItem,
  sortItems,
  clear,
  inputPlaceholder,
  clearTooltipText,
  slot,
  withSeparator,
  disabled,
  width = '340px',
  hasHighestZIndex,
  side = 'bottom',
  align = 'start',
  allLabel,
  maxHeight,
  resultLabel,
  hasSelection
}: AdvancedFilterProps<T>) {
  const [search, setSearch] = useState('');
  const [separatorIndex, setSeparatorIndex] = useState(0);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(DEFAULT_HEADER_HEIGHT);
  const filteredItems = useMemo(() => {
    if (!search) return items;
    const searchLower = search.toLowerCase();
    return items.filter(item => {
      // Search in label
      if (item.label.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search in searchableFields if present
      if (item.searchableFields) {
        return item.searchableFields.some(field => field.toLowerCase().includes(searchLower));
      }
      return false;
    });
  }, [items, search]);
  const selectedItemLabels = useMemo(() => {
    return items.filter(item_0 => item_0.isChecked).map(item_1 => item_1.label);
  }, [items]);
  const isClearable = hasSelection ?? selectedItemLabels.length > 0;

  // Radix sizes the popover to its content, so on a short viewport the footer would sit
  // off-screen. The header is measured rather than assumed because `slot` is arbitrary content
  // and its quick filters wrap. The footer is not subtracted: it overlays the list rather than
  // adding height.
  const listMaxHeight = `min(${maxHeight ?? '360px'}, max(0px, calc(var(--radix-popover-content-available-height) - ${headerHeight}px)))`;
  const handleOpenChange = useCallback((open: boolean) => {
    if (open && sortItems) {
      sortItems();
      setSeparatorIndex(selectedItemLabels.length);
    }
  }, [sortItems, selectedItemLabels]);
  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => scrollElement,
    estimateSize: index => {
      const hasSeparator = withSeparator && !search && separatorIndex > 0 && index === separatorIndex;
      return hasSeparator ? 50 : 33; // 29px item + 4px gap; separator adds ~17px
    },
    overscan: 5,
    getItemKey: index_0 => filteredItems[index_0]!.id
  });
  return <Popover.Root onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <TriggerButton selectedItemLabels={selectedItemLabels} clear={clear} disabled={disabled} clearTooltipText={clearTooltipText} allLabel={allLabel} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content side={side} align={align} sideOffset={5} collisionPadding={15} style={{
        width
      }} className={cn(
      // Base styles
      'flex flex-col', 'bg-popover overflow-hidden rounded-lg', 'shadow-lg',
      // Z-index
      hasHighestZIndex ? 'z-50' : 'z-40',
      // Border
      'border-[0.5px] border-border',
      // Animation
      'data-[state=open]:animate-slide-down-and-fade',
      // Responsive width
      'max-sm:w-[var(--radix-popover-content-available-width)]')}>
          <Command shouldFilter={false} defaultValue="-">
            {/* Search + separator + slot, measured to size the list against the viewport */}
            <div ref={el => {
            if (el) setHeaderHeight(el.offsetHeight);
          }}>
              {/* Input container */}
              <div className="gap-sm pt-sm px-sm pb-xs flex items-center">
                <div className="flex w-full items-center gap-[2px]">
                  <Icon icon={<Search />} className="text-muted-foreground" />
                  <Command.Input placeholder={inputPlaceholder} value={search} onValueChange={setSearch} className={cn('w-full border-none bg-transparent outline-none', 'text-foreground placeholder:text-muted-foreground', 'text-2xs placeholder:text-2xs leading-none placeholder:leading-none')} />
                </div>
              </div>
              <Separator />
              {slot}
            </div>
            <div className="relative">
              <Command.List className="overflow-hidden">
                <div ref={setScrollElement} className={cn('overflow-x-hidden overflow-y-auto',
              // pb lets the last row scroll clear of the footer; scroll-pb keeps cmdk's
              // scrollIntoView from parking a keyboard-selected row behind it.
              'p-2xs pb-[44px] scroll-pb-[44px]')} style={{
                maxHeight: listMaxHeight
              }}>
                  <Command.Group>
                    <div style={{
                    height: virtualizer.getTotalSize(),
                    position: 'relative'
                  }}>
                      {virtualizer.getVirtualItems().map(virtualRow => {
                      const item_2 = filteredItems[virtualRow.index]!;
                      const showSeparator = withSeparator && !search && separatorIndex > 0 && virtualRow.index === separatorIndex;
                      return <div key={item_2.id} style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`
                      }}>
                            {showSeparator && <Separator className="my-xs" />}
                            <AdvancedFilterItem<T> item={item_2} toggleItem={toggleItem} />
                          </div>;
                    })}
                    </div>
                  </Command.Group>
                </div>
              </Command.List>
              <div className="px-sm py-2xs border-border bg-popover absolute inset-x-0 bottom-0 flex items-center justify-between border-t-[0.5px]">
                <span className="text-muted-foreground text-2xs leading-none">
                  {resultLabel ?? (isClearable && selectedItemLabels.length === 0 ? null : `${selectedItemLabels.length} selected`)}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" disabled={!isClearable} onClick={clear} data-testid="filter-clear">
                      Clear
                    </Button>
                  </TooltipTrigger>
                  {isClearable && <TooltipContent>{clearTooltipText}</TooltipContent>}
                </Tooltip>
              </div>
            </div>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>;
}
export default AdvancedFilter;